import { useState } from "react";
import BarraDeNavegacao from "../../componentes/BarraDeNavegacao";
import "./styles.scss";
import { Dialogo } from "@digix-ui/componentes";
import { CampoDeTextoComMascara } from "../../componentes";
import { Controller, useForm } from "react-hook-form";
import AgendaSemanal from "../Agenda/Agenda";
import AtendimentoEmAndamento from "../AtendimentoEmAndamento/AtendimentoEmAndamento";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  criarAgendamento,
  CriarAgendamentoPayload,
} from "../Agenda/services/criarAgendamento";
import { ConverterParaISO } from "../Agenda/utils/toLocalISO";
import { desmascararCpf } from "../Agenda/utils/macaraCPF";
import { mascaraTelefone } from "../Agenda/utils/mascaraTel";

type FormNovoAtendimento = {
  nome: string;
  cpf: string;
  telefone: string;
  data: string;
  horaInicio: string;
  horaFim: string;
};

const hhmmToMinutes = (s?: string) => {
  if (!s) return 0;
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};

const GerenciarAgenda = () => {
  const [abaAtiva, setAbaAtiva] = useState<"todosatendimentos" | "EmAndamento">(
    "todosatendimentos"
  );

  const [modalNovoAtendimento, setModalNovoAtendimento] = useState(false);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormNovoAtendimento>({
    defaultValues: {
      nome: "",
      cpf: "",
      telefone: "",
      data: new Date().toLocaleDateString("pt-BR"),
      horaInicio: "",
      horaFim: "",
    },
  });

  const queryClient = useQueryClient();

  const { mutateAsync: salvarAgendamento, isPending: salvando } = useMutation({
    mutationFn: (payload: CriarAgendamentoPayload) => criarAgendamento(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["agendamentos"] });
      setModalNovoAtendimento(false);
    },
    onError: (error: any) => {
      const resp = error?.response?.data;
      console.error("Erro ao salvar:", resp);
    },
  });

  const onSubmitNovoAtendimento = async (values: FormNovoAtendimento) => {
    const { nome, cpf, telefone, data, horaInicio, horaFim } = values;
    const [dd, mm, yyyy] = (data || "").split("/");
    const base = new Date(Number(yyyy), Number(mm) - 1, Number(dd));

    const payload: CriarAgendamentoPayload = {
      dataInicial: ConverterParaISO(base, horaInicio),
      dataFinal: ConverterParaISO(base, horaFim),
      pessoa: {
        nome,
        cpf: desmascararCpf(cpf),
        telefoneCelular: mascaraTelefone(telefone),
      },
    };

    await salvarAgendamento(payload);
  };

  const corpoDoModalNovoAtendimento = () => (
    <div className="modal-novo-atendimento">
      {/* Nome */}
      <div className="linha">
        <Controller
          name="nome"
          control={control}
          rules={{ required: "Nome é obrigatório." }}
          render={({ field }) => (
            <>
              <CampoDeTextoComMascara
                name="nome"
                label="Nome"
                mascara=""
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                colunas="12"
              />
              {errors.nome && (
                <small className="erro">{errors.nome.message}</small>
              )}
            </>
          )}
        />
      </div>

      <div className="linha linha-horizontal">
        <Controller
          name="cpf"
          control={control}
          rules={{
            required: "CPF é obrigatório.",
            validate: (v) =>
              desmascararCpf(v).length === 11 || "CPF deve conter 11 dígitos.",
          }}
          render={({ field }) => (
            <>
              <CampoDeTextoComMascara
                name="cpf"
                label="CPF"
                mascara="999.999.999-99"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                colunas="6"
              />
              {errors.cpf && (
                <small className="erro">{errors.cpf.message}</small>
              )}
            </>
          )}
        />

        <Controller
          name="telefone"
          control={control}
          rules={{
            required: "Telefone é obrigatório.",
            validate: (v) =>
              mascaraTelefone(v).length >= 10 ||
              "Informe um telefone válido (DDD + número).",
          }}
          render={({ field }) => (
            <>
              <CampoDeTextoComMascara
                name="telefone"
                label="Telefone"
                mascara="(99) 99999-9999"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                colunas="6"
              />
              {errors.telefone && (
                <small className="erro">{errors.telefone.message}</small>
              )}
            </>
          )}
        />
      </div>

      <div className="linha">
        <Controller
          name="data"
          control={control}
          rules={{ required: "Data é obrigatória." }}
          render={({ field }) => (
            <>
              <CampoDeTextoComMascara
                name="data"
                label="Data para abertura"
                mascara="99/99/9999"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                colunas="12"
              />
              {errors.data && (
                <small className="erro">{errors.data.message}</small>
              )}
            </>
          )}
        />
      </div>

      <div className="linha">
        <label>Horário</label>
        <div className="hora-range">
          <Controller
            name="horaInicio"
            control={control}
            rules={{ required: "Horário inicial é obrigatório." }}
            render={({ field }) => (
              <>
                <input
                  type="time"
                  className="formulario__input"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
                {errors.horaInicio && (
                  <small className="erro">{errors.horaInicio.message}</small>
                )}
              </>
            )}
          />

          <span className="ate">até</span>

          <Controller
            name="horaFim"
            control={control}
            rules={{
              required: "Horário final é obrigatório.",
              validate: (value) => {
                const inicio = getValues("horaInicio");
                if (!inicio || !value) return true;
                return (
                  hhmmToMinutes(value) > hhmmToMinutes(inicio) ||
                  "Horário final deve ser maior que o inicial."
                );
              },
            }}
            render={({ field }) => (
              <>
                <input
                  type="time"
                  className="formulario__input"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
                {errors.horaFim && (
                  <small className="erro">{errors.horaFim.message}</small>
                )}
              </>
            )}
          />
        </div>
      </div>
    </div>
  );

  const rodapeDoModalNovoAtendimento = () => (
    <div className="rodape-modal">
      <button
        className="botao botao_contorno botao_cor-secundaria"
        onClick={() => setModalNovoAtendimento(false)}
      >
        Cancelar
      </button>
      <button
        className="botao botao_cor-secundaria botao-confirmar"
        onClick={handleSubmit(onSubmitNovoAtendimento)}
        disabled={salvando}
      >
        {salvando ? "Salvando..." : "Agendar"}
      </button>
    </div>
  );

  return (
    <>
      <BarraDeNavegacao titulo="Agenda" comBotaoADireita={false} />

      <div className="tabs-cadastro__menu">
        <div className="tabs-cadastro__abas">
          <button
            className={abaAtiva === "todosatendimentos" ? "ativo" : ""}
            onClick={() => setAbaAtiva("todosatendimentos")}
          >
            Todos os atendimentos
          </button>
          <button
            className={abaAtiva === "EmAndamento" ? "ativo" : ""}
            onClick={() => setAbaAtiva("EmAndamento")}
          >
            Em andamento
          </button>
        </div>

        <div
          className="botao-customizado"
          onClick={() => setModalNovoAtendimento(true)}
        >
          <i className="fas fa-plus icone" />
          Novo atendimento
        </div>
      </div>

      <div className="tabs-cadastro__conteudo">
        {abaAtiva === "todosatendimentos" && <AgendaSemanal />}
        {abaAtiva === "EmAndamento" && <AtendimentoEmAndamento />}
      </div>

      <Dialogo
        tamanho="mini"
        deveSerExibido={modalNovoAtendimento}
        fecharDialogo={() => setModalNovoAtendimento(false)}
        titulo="Novo atendimento"
        corpo={corpoDoModalNovoAtendimento}
        rodape={rodapeDoModalNovoAtendimento}
      />
    </>
  );
};

export default GerenciarAgenda;
